/**
 * EditorPage
 *
 * Main layout for the V4 CMS editor.
 * Full-screen layout: toolbar at top, iframe preview + edit panel below.
 * Receives websiteId from URL params.
 *
 * Phase 2: Adds section selection, edit panel, staging session.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EditorToolbar } from './components/EditorToolbar';
import { EditorPreview } from './components/EditorPreview';
import { EditPanel } from './components/EditPanel';
import { SectionList } from './components/SectionList';
import { SEOSettings } from './components/SEOSettings';
import { EditModal } from './components/EditModal';
import { ImageEditModal } from './components/ImageEditModal';
import { OnboardingDataPanel } from './components/OnboardingDataPanel';
import { PagesPanel } from './components/PagesPanel';
import { GlobalColorEditor } from './components/GlobalColorEditor';
import { HeaderLinkEditor } from './components/HeaderLinkEditor';
import { AddFormEmbedModal } from './components/AddFormEmbedModal';
import { ClaudeChatPanel } from './components/ClaudeChatPanel';
import { useContentModel } from './hooks/useContentModel';
import { applyStructuralChanges } from '../api/ai-editor';
import { useStagingSession } from './hooks/useStagingSession';
import { useAuth } from '../context/AuthContext';
import { deployStagedChanges, undoLastEdit, getUndoHistory,
  IMAGE_POSITION_SUFFIX,
  CTA_HREF_SUFFIX,
} from '../api/editor';
import type { EditorViewport, EditableSection } from './types';
import type { EditableElement as PanelElement } from './components/EditPanel';
import type { ElementClickInfo } from './components/EditorPreview';

// =============================================================================
// COMPONENT
// =============================================================================

export function EditorPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Content model state
  const contentModel = useContentModel(websiteId);

  // Staging session (lock, save, heartbeat)
  const stagingUser = useMemo(
    () => user ? { id: user.id, name: user.name || user.email } : null,
    [user]
  );
  const staging = useStagingSession(websiteId, stagingUser);

  // Editor UI state
  const [activePage, setActivePage] = useState<string>('home');
  const [viewport, setViewport] = useState<EditorViewport>('desktop');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showFormEmbedModal, setShowFormEmbedModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState<EditableSection | null>(null);

  // Content refresh key — incremented after structural changes to force iframe reload
  const [refreshKey, setRefreshKey] = useState(0);

  // Deploy state
  const [deploying, setDeploying] = useState(false);
  const deployMessageRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deployMessage, setDeployMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [presenceDismissed, setPresenceDismissed] = useState(false);

  // Undo history state
  const [undoCount, setUndoCount] = useState(0);

  // Fetch undo count on mount and after edits
  const refreshUndoCount = useCallback(async () => {
    if (!websiteId) return;
    try {
      const history = await getUndoHistory(websiteId);
      setUndoCount(history.count);
    } catch {
      // Silently fail — undo count is non-critical
    }
  }, [websiteId]);

  // Fetch undo count on mount
  useEffect(() => {
    refreshUndoCount();
  }, [refreshUndoCount]);

  // Sidebar collapsed state (persisted in localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('niftee-editor-sidebar-collapsed');
    return saved === 'true';
  });

  // Sidebar tab state: 'sections', 'client-info', 'colors', or 'claude'
  const [sidebarTab, setSidebarTab] = useState<'sections' | 'pages' | 'client-info' | 'colors' | 'claude'>('sections');

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem('niftee-editor-sidebar-collapsed', String(newValue));
      return newValue;
    });
  }, []);

  const handleDeploy = useCallback(async () => {
    if (!websiteId || deploying) return;
    setDeploying(true);
    setDeployMessage(null);
    try {
      let result = await deployStagedChanges(websiteId);
      // Publishing stops while stand-in content remains. The server's message
      // lists exactly what and where; the person decides.
      if (!result.success && result.needsPlaceholderConfirmation) {
        if (window.confirm(`${result.error}\n\nPublish with these placeholders anyway?`)) {
          result = await deployStagedChanges(websiteId, undefined, true);
        }
      }
      if (result.success) {
        setDeployMessage({
          type: 'success',
          text: `Published v${result.version_number ?? '?'} — ${result.pages_deployed ?? 0} page(s) deployed`,
        });
        // Reset dirty count by resetting staging
        staging.resetDirtyCount();
      } else {
        setDeployMessage({ type: 'error', text: result.error || result.message || 'Publish failed' });
      }
    } catch (err) {
      setDeployMessage({ type: 'error', text: err instanceof Error ? err.message : 'Publish failed' });
    } finally {
      setDeploying(false);
      // Auto-dismiss success message after 5s
      if (deployMessageRef.current) clearTimeout(deployMessageRef.current);
      deployMessageRef.current = setTimeout(() => setDeployMessage(null), 5000);
    }
  }, [websiteId, deploying, staging]);

  // Undo last edit step
  const handleUndo = useCallback(async () => {
    if (!websiteId) return;
    try {
      const result = await undoLastEdit(websiteId);
      if (result.success) {
        // Reload content and preview to reflect restored state
        await contentModel.reload();
        setRefreshKey((k) => k + 1);
        setUndoCount(result.remainingUndoSteps ?? 0);
        setDeployMessage({ type: 'success', text: `Undone: ${result.undone}` });
        if (deployMessageRef.current) clearTimeout(deployMessageRef.current);
        deployMessageRef.current = setTimeout(() => setDeployMessage(null), 3000);
      }
    } catch (err) {
      console.error('[Editor] Undo failed:', err);
    }
  }, [websiteId, contentModel]);

  // Revert all changes
  const handleRevert = useCallback(async () => {
    const success = await staging.revertAll();
    if (success) {
      // Reload content model and refresh preview to show published version
      await contentModel.reload();
      setSelectedSectionId(null);
      setSelectedSection(null);
      setRefreshKey((k) => k + 1);
      setUndoCount(0); // Undo history is cleared on revert
      setDeployMessage({ type: 'success', text: 'All changes reverted to published version' });
      if (deployMessageRef.current) clearTimeout(deployMessageRef.current);
      deployMessageRef.current = setTimeout(() => setDeployMessage(null), 5000);
    }
  }, [staging, contentModel]);

  // Handle content change after structural edits (add/remove/reorder sections)
  const handleContentChange = useCallback(async () => {
    await contentModel.reload();
    setSelectedSectionId(null);
    setSelectedSection(null);
    setRefreshKey((k) => k + 1);
  }, [contentModel]);

  // Element-level modal state
  const [editingElement, setEditingElement] = useState<{
    sectionId: string;
    sectionType: string;
    elementKind: 'heading' | 'text' | 'image' | 'button';
    elementIndex: number;
    currentValue: string;
    /** For images: existing alt text, seeds the AI generate form. */
    currentAlt?: string;
    /** For images: existing `object-position`, seeds the framing control. */
    currentPosition?: string;
    /** Content model element ID, if we can match it */
    contentModelElementId: string | null;
  } | null>(null);

  // Navigate back to site detail page
  const handleBack = useCallback(() => {
    navigate(`/internal-qa/${websiteId}`);
  }, [navigate, websiteId]);

  // Handle page change — close panel and switch pages
  const handlePageChange = useCallback((slug: string) => {
    setActivePage(slug);
    setSelectedSectionId(null);
    setSelectedSection(null);
  }, []);

  // Handle preview error
  const handlePreviewError = useCallback((error: string) => {
    console.error('[Editor] Preview error:', error);
  }, []);

  // Handle section click from iframe
  const handleSectionClick = useCallback(
    (sectionId: string, sectionType: string) => {
      // Find the section in the content model
      // The iframe sends IDs like "hero_0", but our content model uses page-prefixed IDs
      // Try to match by looking at current page's sections
      const sections = contentModel.getSections(activePage);

      // Match by type and index from iframe ID (e.g., "hero_0")
      const matchedSection = sections.find((s) => {
        // Content model section IDs are like "home_hero_0"
        // Iframe section IDs are like "hero_0"
        return s.id.endsWith(`_${sectionId}`) || s.id === sectionId;
      });

      // Also try matching by section type
      const fallbackSection = !matchedSection
        ? sections.find((s) => s.type === sectionType)
        : null;

      const section = matchedSection || fallbackSection;

      if (section) {
        setSelectedSectionId(sectionId);
        setSelectedSection(section);
      } else {
        console.warn(`[Editor] Section not found in content model: ${sectionId} (type: ${sectionType})`);
        // Still select it visually even if we can't find editable elements
        setSelectedSectionId(sectionId);
        setSelectedSection(null);
      }
    },
    [contentModel, activePage]
  );

  // Handle element change from EditPanel
  // Surface a failed/skipped save to the user (reuses the publish message
  // banner) so a save is never silent.
  const showSaveError = useCallback((text: string) => {
    setDeployMessage({ type: 'error', text });
    if (deployMessageRef.current) clearTimeout(deployMessageRef.current);
    deployMessageRef.current = setTimeout(() => setDeployMessage(null), 6000);
  }, []);

  /**
   * Bring the cached content model back in step after a save, once typing stops.
   *
   * `useContentModel` caches, and nothing invalidated it on save — so the panel
   * could repopulate from a stale copy even after navigating away and back.
   * Measured on a live draft: the model and the page markup both held
   * "Upcoming Event" while the sidebar still showed "Registration Open".
   *
   * DEBOUNCED, deliberately. Reloading per keystroke is what made the canvas
   * flash and clobbered in-flight saves the last time I reached for a refresh.
   */
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleModelReload = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null;
      // Not while a field in the panel is being typed in: the reload rebuilds the
      // panel from the server copy. Try again once typing has stopped.
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
        scheduleModelReload();
        return;
      }
      void contentModel.reload();
    }, 2000);
  }, [contentModel]);

  const handleElementChange = useCallback(
    async (elementId: string, newValue: string) => {
      if (!selectedSection) return;

      // Find the element to get its old value and type info
      const element = selectedSection.elements.find((e) => e.id === elementId);
      if (!element) return;

      const oldValue = typeof element.value === 'string' ? element.value : '';

      // 1. Send update to iframe for live preview
      const category = iframeCategory(element);
      const elementsOfSameType = selectedSection.elements.filter(
        (e) => iframeCategory(e) === category
      );
      const elementIndex = elementsOfSameType.indexOf(element);

      if (selectedSectionId) {
        const iframe = document.querySelector('iframe');
        if (iframe?.contentWindow) {
          const categoryType = getCategoryType(element.type);
          if (categoryType === 'image') {
            iframe.contentWindow.postMessage(
              { type: 'niftee-update-image', sectionId: selectedSectionId, elementIndex, elementId, newValue },
              '*'
            );
          } else {
            iframe.contentWindow.postMessage(
              { type: 'niftee-update-text', sectionId: selectedSectionId, elementType: getCategoryForIframe(element), elementIndex, elementId, newValue },
              '*'
            );
          }
        }
      }

      // 2. Save to staging — surface failures instead of swallowing them
      const ok = await staging.saveElement(elementId, oldValue, newValue);
      if (ok) scheduleModelReload();
      if (!ok) {
        showSaveError(staging.lastSaveError() || "Couldn't save that change — it wasn't applied. Please try again.");
        return;
      }

      // 3. Refresh undo count (snapshot was pushed server-side)
      refreshUndoCount();
    },
    [selectedSection, selectedSectionId, staging, refreshUndoCount, showSaveError, scheduleModelReload]
  );

  // Save an image's framing from the side panel.
  //
  // Mirrors handleElementChange, but writes the suffixed key so a reframe does
  // not overwrite the image's source (`changes` is keyed by element id). The
  // panel is a legitimate place to reframe — gallery images are reached there
  // rather than by clicking into the canvas — so it needs its own save path
  // rather than borrowing the modal's.
  const handleElementPositionChange = useCallback(
    async (elementId: string, position: string) => {
      if (!selectedSection) return;

      const element = selectedSection.elements.find((e) => e.id === elementId);
      if (!element) return;

      const previous = (element as { position?: string }).position ?? '50% 50%';
      if (position === previous) return;

      const ok = await staging.saveElement(
        `${elementId}${IMAGE_POSITION_SUFFIX}`,
        previous,
        position
      );
      if (!ok) {
        showSaveError("Couldn't save the framing — it wasn't applied. Please try again.");
        return;
      }

      refreshUndoCount();
      // Reload so the panel thumbnail and the canvas both show the new framing.
      await contentModel.reload();
    },
    [selectedSection, staging, refreshUndoCount, contentModel, showSaveError]
  );

  // Save a button's link target from the side panel.
  const handleElementHrefChange = useCallback(
    async (elementId: string, href: string) => {
      if (!selectedSection) return;

      const element = selectedSection.elements.find((e) => e.id === elementId);
      if (!element) return;

      const previous = (element as { href?: string }).href ?? '';
      if (href === previous) return;

      const ok = await staging.saveElement(`${elementId}${CTA_HREF_SUFFIX}`, previous, href);
      if (!ok) {
        showSaveError("Couldn't save the link — it wasn't applied. Please try again.");
        return;
      }

      refreshUndoCount();
      // Bring the panel back in step with the stored target once typing stops.
      // The canvas is not updated live: an href change alters no visible text,
      // so there is nothing for the preview to repaint. Reloading here directly
      // is what refreshed the screen under the cursor while a link was typed.
      scheduleModelReload();
    },
    [selectedSection, staging, refreshUndoCount, showSaveError, scheduleModelReload]
  );

  // Close edit panel
  const handleClosePanel = useCallback(() => {
    setSelectedSectionId(null);
    setSelectedSection(null);
  }, []);

  // Handle element click from iframe (opens modal)
  const handleElementClick = useCallback(
    (info: ElementClickInfo) => {
      // Also set the section as selected (highlights it + opens side panel)
      const sections = contentModel.getSections(activePage);
      const matchedSection = sections.find(
        (s) => s.id.endsWith(`_${info.sectionId}`) || s.id === info.sectionId
      );
      const fallbackSection = !matchedSection
        ? sections.find((s) => s.type === info.sectionType)
        : null;
      const section = matchedSection || fallbackSection;

      if (section) {
        setSelectedSectionId(info.sectionId);
        setSelectedSection(section);
      }

      // Try to find the matching content model element
      let contentModelElementId: string | null = null;
      let contentModelPosition: string | undefined;

      // A renderer that knows its own element ids stamps them into the markup,
      // and the canvas sends the stamped id back. Trust it outright.
      //
      // The matching below counts the nth element of a guessed CATEGORY, and
      // the category is guessed from label wording — "Event Title" reads as a
      // heading, "Status Badge" as a badge. For a section with NAMED fields the
      // model's order has no relationship to DOM order, so that scrambles them:
      // one eyebrow edit landed on the eyebrow, the tagline and the alt text.
      if (info.elementId && section) {
        const exact = section.elements.find((e) => e.id === info.elementId);
        if (exact) {
          setEditingElement({
            sectionId: info.sectionId,
            sectionType: info.sectionType,
            elementKind: info.elementKind as 'heading' | 'text' | 'image' | 'button',
            elementIndex: info.elementIndex,
            currentValue: info.currentValue,
            currentAlt: info.currentAlt,
            currentPosition: (exact as { position?: string }).position,
            contentModelElementId: exact.id,
          });
          return;
        }
      }

      if (section) {
        const iframeKindToModelType: Record<string, string[]> = {
          heading: ['text', 'rich-text'],
          text: ['text', 'rich-text'],
          badge: ['text', 'rich-text'],
          image: ['image'],
          button: ['link'],
        };
        const matchTypes = iframeKindToModelType[info.elementKind] || ['text'];
        const candidates = section.elements.filter((e) => {
          if (!matchTypes.includes(e.type)) return false;
          // For text elements, differentiate heading vs body by label
          if (info.elementKind === 'heading') {
            const label = e.label.toLowerCase();
            return label.includes('heading') || label.includes('title') || label.includes('name') || label.includes('headline');
          }
          if (info.elementKind === 'badge') {
            return e.label.toLowerCase().includes('badge');
          }
          if (info.elementKind === 'text' && e.type === 'text') {
            const label = e.label.toLowerCase();
            // Badges are also type 'text'; excluding them keeps the body-text
            // index in step with the <p> lookup on the other side.
            if (label.includes('badge')) return false;
            return !label.includes('heading') && !label.includes('title') && !label.includes('headline');
          }
          return true;
        });
        const matched = candidates[info.elementIndex] ?? candidates[0];
        if (matched) {
          contentModelElementId = matched.id;
          contentModelPosition = (matched as { position?: string }).position;
        }
      }

      setEditingElement({
        sectionId: info.sectionId,
        sectionType: info.sectionType,
        elementKind: info.elementKind as 'heading' | 'text' | 'image' | 'button',
        elementIndex: info.elementIndex,
        currentValue: info.currentValue,
        currentAlt: info.currentAlt,
        currentPosition: contentModelPosition,
        contentModelElementId,
      });
    },
    [contentModel, activePage]
  );

  // Handle save from the edit modal (text/heading/button)
  /**
   * Mirror a saved value back into the panel's copy of the section.
   *
   * `selectedSection` is captured when a section is selected and is not
   * re-read after every save. The panel's own fields update as you type, but a
   * save made through the MODAL never reached this copy — so the canvas showed
   * the new value while the sidebar still showed the old one, which reads as a
   * save that half-worked.
   *
   * Modal saves only. Doing this on panel typing would write into the field
   * being typed in, which is how the reload-on-miss regression lost characters.
   */
  const mirrorSavedValue = useCallback(
    (elementId: string, value: string, position?: string) => {
      setSelectedSection((prev) => {
        if (!prev) return prev;
        const i = prev.elements.findIndex((e) => e.id === elementId);
        if (i < 0) return prev;
        const elements = prev.elements.slice();
        elements[i] = { ...elements[i], value, ...(position ? { position } : {}) };
        return { ...prev, elements };
      });
    },
    []
  );

  const handleModalSave = useCallback(
    async (newValue: string) => {
      if (!editingElement) return;
      const el = editingElement;

      // Push a value to the iframe preview (used for the optimistic update and
      // to revert if the save fails).
      const sendPreview = (value: string) => {
        const iframe = document.querySelector('iframe');
        if (!iframe?.contentWindow) return;
        if (el.elementKind === 'image') {
          iframe.contentWindow.postMessage(
            { type: 'niftee-update-image', sectionId: el.sectionId, elementIndex: el.elementIndex, elementId: el.contentModelElementId, newValue: value },
            '*'
          );
        } else {
          iframe.contentWindow.postMessage(
            { type: 'niftee-update-text', sectionId: el.sectionId, elementType: el.elementKind, elementIndex: el.elementIndex, elementId: el.contentModelElementId, newValue: value },
            '*'
          );
        }
      };

      // 1. Optimistic preview
      sendPreview(newValue);

      // 2. Save to staging — surface a skipped or failed save instead of faking it
      if (!el.contentModelElementId) {
        sendPreview(el.currentValue);
        showSaveError("Couldn't match this element to save it. Try reopening the editor, then edit again.");
        return;
      }
      const ok = await staging.saveElement(el.contentModelElementId, el.currentValue, newValue);
      if (!ok) {
        sendPreview(el.currentValue);
        showSaveError("Couldn't save that change — it wasn't applied. Please try again.");
        return;
      }
      mirrorSavedValue(el.contentModelElementId, newValue);
      scheduleModelReload();
      refreshUndoCount();
    },
    [editingElement, staging, refreshUndoCount, showSaveError, mirrorSavedValue, scheduleModelReload]
  );

  // Handle save from the image edit modal (with SEO metadata)
  /**
   * The canvas add-photo tile: which gallery is receiving, or null.
   *
   * The tile sends the MODEL section id (read off the grid id the renderer
   * emits), so no positional lookup is involved.
   */
  const [addingToGallery, setAddingToGallery] = useState<string | null>(null);

  const handleGalleryAdd = useCallback((modelSectionId: string) => {
    setAddingToGallery(modelSectionId);
  }, []);

  /**
   * Add one or more photos in a single request.
   *
   * One applyStructuralChanges call carries N add_gallery_image changes, so a
   * ten-photo upload is one reassembly and one preview refresh, not ten.
   * Framing and caption ride in with a single pick; multi-upload sends url+alt
   * per file.
   */
  const handleAddPhotos = useCallback(
    async (items: Array<{ url: string; alt: string; caption?: string; position?: string }>) => {
      if (!addingToGallery || items.length === 0) return;
      try {
        const result = await applyStructuralChanges(websiteId as string, items.map((item) => ({
          id: crypto.randomUUID(),
          action: 'add_gallery_image' as const,
          target: { page: activePage, section: addingToGallery },
          data: { url: item.url, alt: item.alt, caption: item.caption, position: item.position },
        })));
        if (!result?.success) throw new Error(result?.error || 'Could not add the photos.');
      } catch (err) {
        showSaveError((err as Error).message);
      } finally {
        setAddingToGallery(null);
        await handleContentChange();
      }
    },
    [addingToGallery, websiteId, activePage, showSaveError, handleContentChange]
  );

  /** A tile dragged to a new grid position — one move change, deltas follow. */
  const handleGalleryReorder = useCallback(
    async (modelSectionId: string, from: number, to: number) => {
      try {
        const result = await applyStructuralChanges(websiteId as string, [{
          id: crypto.randomUUID(),
          action: 'move_gallery_image' as const,
          target: { page: activePage, section: modelSectionId, index: from },
          data: { toIndex: to },
        }]);
        if (!result?.success) throw new Error(result?.error || 'Could not reorder the photos.');
      } catch (err) {
        showSaveError((err as Error).message);
      } finally {
        await handleContentChange();
      }
    },
    [websiteId, activePage, showSaveError, handleContentChange]
  );

  /** The tile's two-step remove was confirmed on the canvas. */
  const handleGalleryRemove = useCallback(
    async (modelSectionId: string, index: number) => {
      try {
        const result = await applyStructuralChanges(websiteId as string, [{
          id: crypto.randomUUID(),
          action: 'remove_gallery_image' as const,
          target: { page: activePage, section: modelSectionId, index },
          data: {},
        }]);
        if (!result?.success) throw new Error(result?.error || 'Could not remove the photo.');
      } catch (err) {
        showSaveError((err as Error).message);
      } finally {
        await handleContentChange();
      }
    },
    [websiteId, activePage, showSaveError, handleContentChange]
  );

  const handleImageModalSave = useCallback(
    async (newUrl: string, _altText: string, _title: string, position: string) => {
      if (!editingElement) return;

      // 1. Update iframe DOM live
      const iframe = document.querySelector('iframe');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: 'niftee-update-image',
            sectionId: editingElement.sectionId,
            elementIndex: editingElement.elementIndex,
            elementId: editingElement.contentModelElementId,
            newValue: newUrl,
          },
          '*'
        );
      }

      // 2. Save to staging — surface a skipped or failed save instead of faking it
      if (!editingElement.contentModelElementId) {
        // Revert the optimistic preview to the original image
        const iframeEl = document.querySelector('iframe');
        iframeEl?.contentWindow?.postMessage(
          { type: 'niftee-update-image', sectionId: editingElement.sectionId, elementIndex: editingElement.elementIndex, elementId: editingElement.contentModelElementId, newValue: editingElement.currentValue },
          '*'
        );
        showSaveError("Couldn't match this image to save it. Try reopening the editor, then swap again.");
        return;
      }
      const ok = await staging.saveElement(
        editingElement.contentModelElementId,
        editingElement.currentValue,
        newUrl
      );
      if (!ok) {
        const iframeEl = document.querySelector('iframe');
        iframeEl?.contentWindow?.postMessage(
          { type: 'niftee-update-image', sectionId: editingElement.sectionId, elementIndex: editingElement.elementIndex, elementId: editingElement.contentModelElementId, newValue: editingElement.currentValue },
          '*'
        );
        showSaveError("Couldn't save the image — it wasn't applied. Please try again.");
        return;
      }
      // 3. Save the framing, if it moved.
      //
      // Sent under a suffixed key because `changes` is keyed by element id: a
      // swap and a reframe of the same image would otherwise collide and the
      // later write would erase the earlier one. The server applies sources
      // first and framing second, so this ordering matches what it expects.
      const previousPosition = editingElement.currentPosition ?? '50% 50%';
      if (position && position !== previousPosition) {
        const framed = await staging.saveElement(
          `${editingElement.contentModelElementId}${IMAGE_POSITION_SUFFIX}`,
          previousPosition,
          position
        );
        if (!framed) {
          showSaveError("The image saved, but its framing didn't. Reopen and set it again.");
        }
      }

      // Mirror into the panel's copy directly. Reloading the content model
      // below refreshes the model, but `selectedSection` is separate state and
      // is not re-derived from it — which is why the panel thumbnail kept
      // showing the previous image after a swap.
      mirrorSavedValue(editingElement.contentModelElementId, newUrl, position || undefined);

      refreshUndoCount();
      // 4. Refresh the content model so the side-panel thumbnail reflects the swap.
      await contentModel.reload();

      // TODO: Save alt text and title to content model when supported
      // For now, alt/title are stored on the media record in website_media

      setEditingElement(null);
    },
    [editingElement, staging, refreshUndoCount, contentModel, showSaveError, mirrorSavedValue]
  );

  // Close edit modal
  const handleModalClose = useCallback(() => {
    setEditingElement(null);
  }, []);

  // Set initial page when content loads
  const hasContent = contentModel.hasContent;
  const pageList = contentModel.pageList;

  // Set activePage to first page if current page isn't in the list
  if (hasContent && pageList.length > 0) {
    const currentPageExists = pageList.some((p) => p.slug === activePage);
    if (!currentPageExists && pageList[0].slug !== activePage) {
      setTimeout(() => setActivePage(pageList[0].slug), 0);
    }
  }

  // Loading state
  if (contentModel.isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-500 text-sm">Loading editor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (contentModel.error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to load editor</h2>
          <p className="text-gray-500 text-sm mb-4">{contentModel.error}</p>
          <button
            onClick={handleBack}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Go back
          </button>
        </div>
      </div>
    );
  }

  // No content state
  if (!contentModel.hasContent) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No content yet</h2>
          <p className="text-gray-500 text-sm mb-4">
            Generate the website first, then come back here to edit it.
          </p>
          <button
            onClick={handleBack}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Go back
          </button>
        </div>
      </div>
    );
  }

  // Compute section index for EditPanel controls
  const currentPageSections = contentModel.getSections(activePage);

  // Extract current page SEO data for the SEO settings panel
  const currentPageData = contentModel.getPage(activePage);
  const currentPageTitle = currentPageData?.title || '';
  const currentPageDescription = currentPageData?.description || '';
  const currentPageKeywords = currentPageData?.keywords || '';
  const sectionIndex = selectedSection
    ? currentPageSections.findIndex((s) => s.id === selectedSection.id)
    : -1;

  // Render the panel from the LIVE section (fresh from the content model) rather
  // than the snapshot captured at click time, so a swap shows in the panel after
  // contentModel.reload() — not just in the iframe preview.
  const liveSelectedSection = selectedSection
    ? currentPageSections.find((s) => s.id === selectedSection.id) ?? selectedSection
    : null;

  // Convert content model elements to EditPanel format
  const panelElements: PanelElement[] = liveSelectedSection
    ? liveSelectedSection.elements
        .filter((e) => e.editable !== false)
        .map((e) => ({
          id: e.id,
          type: mapElementType(e.type),
          label: e.label,
          value: typeof e.value === 'string' ? e.value : Array.isArray(e.value) ? e.value.join('\n') : '',
          placeholder: e.placeholder,
          // Carried explicitly: this map rebuilds the element rather than
          // spreading it, so anything omitted here is silently lost. Without
          // it the framing control always opens centred and the panel
          // thumbnail never shows the framing the page is using.
          position: (e as { position?: string }).position,
          // Carried explicitly for the same reason as position: this map
          // rebuilds the element rather than spreading it, so anything omitted
          // here is silently lost — the link field would just render empty.
          href: (e as { href?: string }).href,
          constraints: e.constraints,
        }))
    : [];

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* Toolbar */}
      <EditorToolbar
        businessName={contentModel.website?.businessName || 'Site'}
        activePage={activePage}
        pages={pageList}
        viewport={viewport}
        zoomLevel={zoomLevel}
        dirtyCount={staging.dirtyCount}
        deploying={deploying}
        websiteId={websiteId}
        undoCount={undoCount}
        onPageChange={handlePageChange}
        onViewportChange={setViewport}
        onZoomChange={setZoomLevel}
        onBack={handleBack}
        onDeploy={handleDeploy}
        onUndo={handleUndo}
        onRevert={handleRevert}
        onContentChange={handleContentChange}
      />

      {/* Shared-draft presence notice (non-blocking) */}
      {staging.presence && !presenceDismissed && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800">
          <span>
            Heads up: this draft was started by <strong>{staging.presence.name}</strong> ({staging.presence.type}).
            You're sharing one draft — edits combine, and Publish applies everyone's.
          </span>
          <button
            onClick={() => setPresenceDismissed(true)}
            className="shrink-0 font-medium text-amber-700 hover:text-amber-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Deploy feedback toast */}
      {deployMessage && (
        <div
          className={`absolute top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium ${
            deployMessage.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {deployMessage.text}
        </div>
      )}

      {/* Main area: preview + optional edit panel */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Preview area */}
        <div className="flex-1 relative overflow-hidden">
          {websiteId && (
            <EditorPreview
              websiteId={websiteId}
              pageSlug={activePage}
              viewport={viewport}
              zoomLevel={zoomLevel}
              selectedSectionId={selectedSectionId}
              refreshKey={refreshKey}
              onError={handlePreviewError}
              onNavigate={handlePageChange}
              onSectionClick={handleSectionClick}
              onElementClick={handleElementClick}
              onGalleryAdd={handleGalleryAdd}
              onGalleryReorder={handleGalleryReorder}
              onGalleryRemove={handleGalleryRemove}
            />
          )}
        </div>

        {/* Sidebar toggle button */}
        <button
          onClick={toggleSidebar}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-l-lg px-1.5 py-3 shadow-md hover:bg-gray-50 transition-all"
          style={{ right: sidebarCollapsed ? 0 : 320 }}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          <svg
            className={`w-4 h-4 text-gray-600 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Edit Panel / Section List / Client Info (collapsible sidebar) */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden flex flex-col ${
            sidebarCollapsed ? 'w-0' : 'w-80'
          }`}
        >
          {!sidebarCollapsed && (
            <>
              {/* Show EditPanel when a section is selected (overrides tabs) */}
              {selectedSection && websiteId ? (
                <EditPanel
                  sectionId={selectedSection.id}
                  sectionLabel={selectedSection.label}
                  sectionType={selectedSection.type}
                  elements={panelElements}
                  websiteId={websiteId}
                  activePage={activePage}
                  sectionIndex={sectionIndex}
                  totalSections={currentPageSections.length}
                  pageSectionIds={currentPageSections.map((s) => s.id)}
                  onChange={handleElementChange}
                  onPositionChange={handleElementPositionChange}
                  onHrefChange={handleElementHrefChange}
                  onClose={handleClosePanel}
                  onContentChange={handleContentChange}
                />
              ) : (
                <>
                  {/* Tabs for Sections / Colors / Client Info */}
                  <div className="flex border-b border-gray-200 bg-white">
                    <button
                      onClick={() => setSidebarTab('sections')}
                      className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                        sidebarTab === 'sections'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      Page Info
                    </button>
                    <button
                      onClick={() => setSidebarTab('pages')}
                      className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                        sidebarTab === 'pages'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      Pages
                    </button>
                    <button
                      onClick={() => setSidebarTab('colors')}
                      className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                        sidebarTab === 'colors'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      Colors
                    </button>
                    <button
                      onClick={() => setSidebarTab('client-info')}
                      className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                        sidebarTab === 'client-info'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      Client
                    </button>
                    <button
                      onClick={() => setSidebarTab('claude')}
                      className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                        sidebarTab === 'claude'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      niftee AI
                    </button>
                  </div>

                  {/* Tab content */}
                  <div className="flex-1 overflow-hidden overflow-y-auto">
                    {sidebarTab === 'sections' && (
                      <div className="h-full flex flex-col">
                        <SEOSettings
                          websiteId={websiteId || ''}
                          activePage={activePage}
                          pageTitle={currentPageTitle}
                          pageDescription={currentPageDescription}
                          pageKeywords={currentPageKeywords}
                          pageSlug={activePage}
                          onSeoChanged={() => {
                            contentModel.reload();
                            setRefreshKey((k) => k + 1);
                            refreshUndoCount();
                          }}
                        />
                        <SectionList
                          websiteId={websiteId || ''}
                          activePage={activePage}
                          sections={currentPageSections}
                          onSectionSelect={(sectionId) => {
                            const section = currentPageSections.find((s) => s.id === sectionId);
                            if (section) {
                              // Extract the iframe-compatible section ID (e.g., "hero_0" from "home_hero_0")
                              const parts = section.id.split('_');
                              const iframeSectionId = parts.length >= 3
                                ? parts.slice(1).join('_')
                                : section.id;
                              handleSectionClick(iframeSectionId, section.type);
                            }
                          }}
                          onContentChange={handleContentChange}
                        />
                        <div className="px-4 py-3 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => setShowFormEmbedModal(true)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Embedded form
                          </button>
                          <p className="text-xs text-gray-400 mt-1.5">
                            Jotform, Google Forms or Sprout Studio. Responses go to the customer's
                            own account, not the niftee leads inbox.
                          </p>
                        </div>
                      </div>
                    )}
                    {sidebarTab === 'pages' && (
                      <div className="h-full overflow-y-auto">
                        <PagesPanel
                          websiteId={websiteId || ''}
                          pageList={pageList}
                          navigation={contentModel.editableContent?.navigation ?? null}
                          activePage={activePage}
                          onSelectPage={handlePageChange}
                          onPagesChanged={() => {
                            // Every page-management op rewrites the scaffold on
                            // all pages, so reload the model rather than patching
                            // local state, and force the iframe to re-fetch.
                            void contentModel.reload();
                            setRefreshKey((k) => k + 1);
                            refreshUndoCount();
                          }}
                        />
                        {/* The header link lives with navigation, not colours:
                            it is a link in the top bar, and it reaches the page
                            through the same scaffold rebuild the nav uses. */}
                        <HeaderLinkEditor
                          websiteId={websiteId || ''}
                          onLinkChanged={() => {
                            void contentModel.reload();
                            setRefreshKey((k) => k + 1);
                            refreshUndoCount();
                          }}
                        />
                      </div>
                    )}
                    {sidebarTab === 'colors' && (
                      <div className="p-3">
                        <GlobalColorEditor
                          websiteId={websiteId || ''}
                          onColorsChanged={() => {
                            // Refresh preview after color change
                            setRefreshKey((k) => k + 1);
                            refreshUndoCount();
                          }}
                        />
                      </div>
                    )}
                    {sidebarTab === 'client-info' && (
                      <OnboardingDataPanel
                        websiteId={websiteId || ''}
                        onBusinessInfoChanged={() => {
                          // Refresh preview after contact info changes
                          setRefreshKey((k) => k + 1);
                          refreshUndoCount();
                        }}
                      />
                    )}
                    {sidebarTab === 'claude' && (
                      <ClaudeChatPanel
                        websiteId={websiteId || ''}
                        selectedSection={selectedSectionId || undefined}
                        currentPage={activePage}
                        onActionsExecuted={() => {
                          // Refresh preview after AI makes changes
                          setRefreshKey((k) => k + 1);
                          refreshUndoCount();
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal — image elements get the full ImageEditModal, others get the text EditModal */}
      {addingToGallery && websiteId && (
        <ImageEditModal
          currentValue=""
          label="Add photos"
          mode="add"
          enableFraming
          websiteId={websiteId}
          onAddMany={(items) => void handleAddPhotos(items)}
          onSave={(url, alt, caption, position) =>
            void handleAddPhotos([{ url, alt, caption: caption || undefined, position: position || undefined }])
          }
          onClose={() => setAddingToGallery(null)}
        />
      )}

      {editingElement && editingElement.elementKind === 'image' && websiteId ? (
        <ImageEditModal
          currentValue={editingElement.currentValue}
          label={getModalLabel(editingElement)}
          currentAlt={editingElement.currentAlt}
          currentPosition={editingElement.currentPosition}
          enableFraming
          websiteId={websiteId}
          onSave={handleImageModalSave}
          onClose={handleModalClose}
        />
      ) : editingElement ? (
        <EditModal
          elementKind={editingElement.elementKind}
          currentValue={editingElement.currentValue}
          label={getModalLabel(editingElement)}
          websiteId={websiteId}
          elementLabel={getModalLabel(editingElement)}
          onSave={handleModalSave}
          onClose={handleModalClose}
        />
      ) : null}

      {showFormEmbedModal && (
        <AddFormEmbedModal
          websiteId={websiteId || ''}
          pageSlug={activePage}
          onClose={() => setShowFormEmbedModal(false)}
          onAdded={() => {
            void contentModel.reload();
            setRefreshKey((k) => k + 1);
            refreshUndoCount();
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

/** Map content model element types to EditPanel types */
function mapElementType(type: string): 'text' | 'image' | 'cta' {
  switch (type) {
    case 'image':
      return 'image';
    case 'link':
      return 'cta';
    default:
      return 'text';
  }
}

/** Get the broad category type for iframe messaging */
function getCategoryType(type: string): string {
  if (type === 'image') return 'image';
  if (type === 'link') return 'cta';
  return 'text';
}

/** Get the iframe-compatible element type for text update messages */
function getCategoryForIframe(element: { id?: string; type: string; label: string }): string {
  if (element.type === 'link') return 'cta';

  // Prefer the element id. The parser stamps an unambiguous namespace into it
  // — _heading_, _text_, _badge_, _image_, _cta_ — one per extractor, so it
  // cannot drift the way label wording does.
  //
  // Label matching had already drifted: headings are labelled "Hero Headline",
  // and this function tested for "heading", "title" and "name" but NOT
  // "headline", so it classified every headline as body text and sent it to
  // the <p> lookup. handleElementClick checks "headline" and did not agree —
  // the two halves of the same decision disagreeing is the kind of thing that
  // only shows up as "the preview did not update".
  const id = element.id || '';
  if (id.includes('_badge_')) return 'badge';
  if (id.includes('_heading_')) return 'heading';
  if (id.includes('_text_')) return 'text';

  // Fall back to the label for anything not carrying a parser id.
  const label = element.label.toLowerCase();
  if (label.includes('badge')) return 'badge';
  if (
    label.includes('heading') ||
    label.includes('headline') ||
    label.includes('title') ||
    label.includes('name')
  ) {
    return 'heading';
  }
  return 'text';
}

/**
 * The category an element belongs to for index purposes.
 *
 * The index the panel sends is resolved by the iframe against a selector, so
 * the two must count the same set. They did not: `getCategoryType` groups
 * headings and paragraphs together (both are model type 'text') while the
 * iframe indexes `h1-h6` and `p` separately. On live content 43 of 98 text
 * elements were misaligned by exactly the heading count — so editing body text
 * from the panel updated nothing, or the wrong node, and the preview only
 * caught up on a page reload.
 */
function iframeCategory(element: { id?: string; type: string; label: string }): string {
  if (element.type === 'image') return 'image';
  return getCategoryForIframe(element);
}

/** Generate a human-readable label for the edit modal */
function getModalLabel(element: {
  sectionType: string;
  elementKind: string;
  elementIndex: number;
}): string {
  const sectionName = element.sectionType.charAt(0).toUpperCase() + element.sectionType.slice(1);
  const kindLabels: Record<string, string> = {
    heading: 'Heading',
    text: 'Text',
    image: 'Image',
    button: 'Button',
  };
  const kindLabel = kindLabels[element.elementKind] || 'Element';
  const indexSuffix = element.elementIndex > 0 ? ` ${element.elementIndex + 1}` : '';
  return `${sectionName} — ${kindLabel}${indexSuffix}`;
}
